import React, { CSSProperties, useEffect, useRef } from 'react'

import styles from './Popover.module.scss'
import { PopoverProps } from './Popover.types'

export const Popover: React.FC<PopoverProps> = ({
    children,
    isOpen,
    onClose,
    trigger,
    width,
    maxWidth,
    height,
    maxHeight,
    position = 'bottom-left',
    className = '',
    offset = 8
}) => {
    const popoverRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!isOpen || !onClose) return

        const handleClickOutside = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                onClose()
            }
        }

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [isOpen, onClose])

    const positionClass = {
        'bottom-left': styles.bottomLeft,
        'bottom-right': styles.bottomRight,
        'bottom-center': styles.bottomCenter,
        'top-left': styles.topLeft,
        'top-right': styles.topRight,
        'top-center': styles.topCenter
    }[position]

    const contentStyle: CSSProperties = {
        ...(width && { width }),
        ...(maxWidth && { maxWidth }),
        ...(height && { height }),
        ...(maxHeight && { maxHeight }),
        '--offset': `${offset}px`
    } as CSSProperties

    return (
        <div className={styles.popoverContainer} ref={popoverRef}>
            {trigger}
            {isOpen && (
                <div
                    className={`${styles.popoverContent} ${positionClass} ${className}`}
                    style={contentStyle}
                >
                    {children}
                </div>
            )}
        </div>
    )
}

Popover.displayName = 'Popover'
